import jsonFromDocument from "cerulean/src/JsonFromDocument";
import { useUserConfiguration } from "../../../hooks/useUserConfiguration.ts";
import { canViewGuard } from "../../../hooks/permissions/useGuardEnabled.ts";
import { isSyncSettingsEnabled } from "../../../pages/configuration/settings_sync/isSyncSettingsEnabled.ts";
import { NavigationRoutes } from "../constants/navigationRoutes.ts";

export function useRoutesEnabled(): {
  [key in NavigationRoutes]: boolean;
} {
  const userConfiguration = useUserConfiguration();
  const { isFeatureEnabled, features, permissions, roles } = userConfiguration;
  const isDemo = Boolean(jsonFromDocument("institution_is_demo", "false"));
  const isLocal = Boolean(jsonFromDocument("institution_is_local", "false"));

  const isDigitalBankingEnabled =
    features.includes("online_banking") || features.includes("mobile_banking");

  const isAccountOpeningEnabled = features.includes("new_account_opening");

  const isStaffLedAccountOpeningEnabled =
    features.includes("staffer_led_ao_consumer") || features.includes("staffer_led_ao_business");

  const isLendingEnabled = features.includes("lending");

  const isCopyEditorEnabled =
    (features.includes("copyeditor") || features.includes("multilingual")) && roles.superUser;

  const isConversionPortalEnabled =
    features.includes("conversion_portal") &&
    permissions.includes("backoffice.use_conversion_portal");

  return {
    [NavigationRoutes.InstitutionSettings]:
      // Demo envs can default to the legacy link, even for Narmi users.
      !features.includes("institution_settings_redesign") &&
      (permissions.includes("backoffice.change_settings") ||
        permissions.includes("backoffice.view_settings")) &&
      ((roles.narmi && isDemo) || (!roles.narmi && !isLocal)),
    [NavigationRoutes.InstitutionSettingsV2]:
      // All local users and Narmi users outside of sales demo envs always get the redesign link regardless of flag,
      // assuming they have the right permissions.
      (isLocal || (roles.narmi && !isDemo) || features.includes("institution_settings_redesign")) &&
      (permissions.includes("backoffice.change_settings") ||
        permissions.includes("backoffice.view_settings")),
    [NavigationRoutes.RiskManager]:
      isDigitalBankingEnabled && permissions.includes("backoffice.view_workflowrule"),
    [NavigationRoutes.RiskRules]:
      isDigitalBankingEnabled && permissions.includes("backoffice.change_workflowrule"),
    [NavigationRoutes.CopyEditor]: isCopyEditorEnabled || roles.narmi,
    [NavigationRoutes.CopyEditorSettings]: roles.narmi,
    [NavigationRoutes.CopyEditorServer]: roles.narmi,
    [NavigationRoutes.CopyEditorOlb]:
      (isCopyEditorEnabled && isDigitalBankingEnabled) || roles.narmi,
    [NavigationRoutes.CopyEditorBao]:
      (isCopyEditorEnabled && isAccountOpeningEnabled) || roles.narmi,
    [NavigationRoutes.CopyEditorCao]:
      (isCopyEditorEnabled && isAccountOpeningEnabled) || roles.narmi,
    [NavigationRoutes.CopyEditorSao]:
      (isCopyEditorEnabled && isStaffLedAccountOpeningEnabled) || roles.narmi,
    [NavigationRoutes.CopyEditorApp]:
      (isCopyEditorEnabled && isDigitalBankingEnabled && features.includes("mobile_banking")) ||
      roles.narmi,
    [NavigationRoutes.CopyEditorLending]: (isCopyEditorEnabled && isLendingEnabled) || roles.narmi,
    [NavigationRoutes.TestNotifications]: permissions.includes(
      "backoffice.test_notification_sender"
    ),
    [NavigationRoutes.TestCoreConnectivity]: roles.narmi,
    [NavigationRoutes.NotificationHistory]: permissions.includes(
      "backoffice.view_notification_history"
    ),
    [NavigationRoutes.NarmiAdmin]: roles.narmi,
    [NavigationRoutes.Preburner]: roles.narmi && features.includes("preburner"),

    [NavigationRoutes.Dashboard]: true,
    [NavigationRoutes.Inbox]: true,
    [NavigationRoutes.DepositApplications]:
      isAccountOpeningEnabled && permissions.includes("indigo.view_accountapplicationapplicants"),
    [NavigationRoutes.LoanApplications]:
      isLendingEnabled &&
      (permissions.includes("lending_api.view_personalloanapplication") ||
        permissions.includes("lending_api.view_creditcardapplication")),
    [NavigationRoutes.MembershipCardUploads]: roles.narmi && isAccountOpeningEnabled,
    [NavigationRoutes.ProductListManager]:
      features.includes("new_account_opening") &&
      permissions.includes("indigo.view_opening_products"),
    [NavigationRoutes.ProductListManagerV2]:
      roles.narmi &&
      features.includes("new_account_opening") &&
      features.includes("product_list_manager_v2") &&
      permissions.includes("indigo.view_opening_products"),
    [NavigationRoutes.Personas]:
      features.includes("personas") && permissions.includes("indigo.view_persona"),
    [NavigationRoutes.ManageUsers]:
      isDigitalBankingEnabled && permissions.includes("auth.view_user"),
    [NavigationRoutes.StaffBulkUpload]: roles.narmi,
    [NavigationRoutes.ManageRoles]: permissions.includes("backoffice.view_group"),
    [NavigationRoutes.ACHManager]:
      isDigitalBankingEnabled &&
      (permissions.includes("indigo.process_achtransaction") ||
        permissions.includes("indigo.view_achtransaction") ||
        permissions.includes("indigo.process_achapplicationtransaction") ||
        permissions.includes("indigo.view_achapplicationtransaction")),
    [NavigationRoutes.AOAchManager]:
      features.includes("new_account_opening") &&
      (permissions.includes("indigo.process_achtransaction") ||
        permissions.includes("indigo.view_achtransaction") ||
        permissions.includes("indigo.process_achapplicationtransaction") ||
        permissions.includes("indigo.view_achapplicationtransaction")) &&
      isFeatureEnabled("ach_manager_display", "V2_AO"),
    [NavigationRoutes.PaymentsAchManager]:
      isDigitalBankingEnabled &&
      features.includes("ach_payments_staff_management") &&
      (permissions.includes("indigo.process_achtransaction") || // confirm with DB what permissions are needed
        permissions.includes("indigo.view_achtransaction")),
    [NavigationRoutes.PassthruAchManager]:
      isDigitalBankingEnabled &&
      features.includes("ach_nacha_passthrough") &&
      permissions.includes("indigo.view_nachapassthroughsubmission"),
    [NavigationRoutes.WireManager]:
      isDigitalBankingEnabled &&
      (isFeatureEnabled("wire_manager_display", "wire_manager_v2") ||
        isFeatureEnabled("wire_manager_display", "manual_processing")) &&
      (permissions.includes("indigo.change_wiretransaction") ||
        permissions.includes("indigo.view_wiretransaction")),
    [NavigationRoutes.IntlWireManager]:
      isDigitalBankingEnabled &&
      features.includes("international_wires_staff_management") &&
      permissions.includes("indigo.view_swiftwiretransaction"),
    [NavigationRoutes.FedNowManager]:
      isDigitalBankingEnabled &&
      (features.includes("fednow_receive") || features.includes("fednow_send")) &&
      permissions.includes("backoffice.view_fednow_payment"),
    [NavigationRoutes.Messages]:
      isDigitalBankingEnabled && permissions.includes("indigo.view_message"),
    [NavigationRoutes.Reports]: permissions.includes("backoffice.view_reports"),
    [NavigationRoutes.Promotions]:
      features.includes("marketing") &&
      (permissions.includes("backoffice.view_marketing") ||
        permissions.includes("backoffice.update_marketing")),
    [NavigationRoutes.Apps]:
      features.includes("naf_app_management") &&
      (permissions.includes("public_api.list_oauth_applications") ||
        permissions.includes("public_api.list_own_oauth_applications")),
    [NavigationRoutes.OauthApps]:
      permissions.includes("public_api.list_oauth_applications") ||
      permissions.includes("public_api.list_own_oauth_applications"),
    [NavigationRoutes.Functions]:
      features.includes("narmi_functions") &&
      (permissions.includes("backoffice.view_narmifunction") ||
        permissions.includes("backoffice.view_own_narmifunction")),
    [NavigationRoutes.SSO]: permissions.includes("indigo.view_foreignopenidconnectapplication"),
    [NavigationRoutes.ManageStaff]: permissions.includes("backoffice.view_staff_user"),
    [NavigationRoutes.Guides]:
      isDigitalBankingEnabled &&
      (features.includes("onboarding_prompts") || features.includes("existing_user_prompts")) &&
      permissions.includes("indigo.view_promptgroup"),
    [NavigationRoutes.Conversions]: roles.narmi && features.includes("conversions_dashboard"),
    [NavigationRoutes.ConversionDataViewer]:
      roles.narmi && features.includes("conversions_dashboard"),
    [NavigationRoutes.ConversionPortalHome]: isConversionPortalEnabled,
    [NavigationRoutes.ConversionPortalUsers]: isConversionPortalEnabled,
    [NavigationRoutes.ConversionPortalExclusions]: isConversionPortalEnabled,
    [NavigationRoutes.ConversionPortalIssues]: isConversionPortalEnabled,
    [NavigationRoutes.Deconversions]: roles.narmi && features.includes("deconversions"),
    [NavigationRoutes.MessagesConfiguration]:
      isDigitalBankingEnabled && permissions.includes("indigo.view_message"),
    [NavigationRoutes.ApplicationsConfiguration]:
      features.includes("application_decisioning") &&
      features.includes("new_account_opening") &&
      permissions.includes("backoffice.manage_application_outcomes_and_reasons"),
    [NavigationRoutes.SftpWizard]:
      roles.narmi && permissions.includes("backoffice.change_settings"),
    [NavigationRoutes.SettingsSync]: isSyncSettingsEnabled({ roles, features, isLocal, isDemo }),
    [NavigationRoutes.Guard]: canViewGuard(userConfiguration),
    [NavigationRoutes.Survey]: permissions.includes("indigo.view_survey"),
    [NavigationRoutes.Audiences]:
      permissions.includes("indigo.view_audience") && features.includes("audiences"),
    [NavigationRoutes.WireFxRates]: permissions.includes("backoffice.view_fx_rates"),
  };
}
